import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import "./ServiceTrackingPage.css";

type TrackingHistory = {
  status: string;
  note: string | null;
  created_at: string;
};

type TrackingData = {
  order_number: string;
  status: string;
  requested_service: string | null;
  customer_report: string | null;
  received_at: string;
  estimated_delivery_at: string | null;
  ready_at: string | null;
  delivered_at: string | null;
  updated_at: string;
  history: TrackingHistory[];
  visible_evidence_count: number;
};

const STATUS_LABELS: Record<string, string> = {
  received: "Recibida",
  inspection: "Inspección",
  diagnosing: "Diagnóstico",
  awaiting_approval: "Esperando aprobación",
  approved: "Aprobada",
  in_progress: "En servicio",
  testing: "Pruebas",
  ready: "Terminada",
  delivered: "Entregada",
  cancelled: "Cancelada",
};

function datetime(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("es-PE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function ServiceTrackingPage() {
  const { token = "" } = useParams();
  const [data, setData] = useState<TrackingData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError(null);

      const { data: result, error: rpcError } = await supabase.rpc(
        "get_service_tracking_by_token",
        { p_token: token },
      );

      if (!active) return;

      if (rpcError || !result) {
        setData(null);
        setError(
          rpcError?.message ??
            "El enlace de seguimiento no existe o ya no está disponible.",
        );
        setLoading(false);
        return;
      }

      setData(result as TrackingData);
      setLoading(false);
    }

    void load();
    const timer = window.setInterval(() => void load(), 30000);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [token]);

  const progress = useMemo(() => {
    const order = [
      "received",
      "inspection",
      "diagnosing",
      "awaiting_approval",
      "approved",
      "in_progress",
      "testing",
      "ready",
      "delivered",
    ];
    if (!data) return 0;
    const index = order.indexOf(data.status);
    return index < 0 ? 0 : Math.round(((index + 1) / order.length) * 100);
  }, [data]);

  if (loading && !data) {
    return (
      <main className="tracking-page">
        <section className="tracking-card">
          <strong>Cargando seguimiento…</strong>
        </section>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="tracking-page">
        <section className="tracking-card">
          <span className="tracking-eyebrow">Seguimiento de servicio</span>
          <h1>Enlace no disponible</h1>
          <p>{error}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="tracking-page">
      <section className="tracking-card tracking-card-main">
        <span className="tracking-eyebrow">Seguimiento de servicio</span>
        <h1>{data.order_number}</h1>
        <p className="tracking-status">
          {STATUS_LABELS[data.status] ?? data.status}
        </p>

        <div className="tracking-progress">
          <div style={{ width: `${progress}%` }} />
        </div>

        <div className="tracking-summary-grid">
          <div>
            <span>Ingreso</span>
            <strong>{datetime(data.received_at)}</strong>
          </div>
          <div>
            <span>Entrega estimada</span>
            <strong>{datetime(data.estimated_delivery_at)}</strong>
          </div>
          <div>
            <span>Última actualización</span>
            <strong>{datetime(data.updated_at)}</strong>
          </div>
          <div>
            <span>Evidencias visibles</span>
            <strong>{Number(data.visible_evidence_count || 0)}</strong>
          </div>
        </div>

        {data.requested_service ? (
          <div className="tracking-note">
            <span>Servicio solicitado</span>
            <p>{data.requested_service}</p>
          </div>
        ) : null}

        <section className="tracking-history">
          <h2>Historial</h2>
          {(data.history ?? []).map((item, index) => (
            <article key={item.created_at + index}>
              <div>
                <strong>{STATUS_LABELS[item.status] ?? item.status}</strong>
                <time>{datetime(item.created_at)}</time>
              </div>
              {item.note ? <p>{item.note}</p> : null}
            </article>
          ))}
        </section>

        <footer>© 2026 - CORE SAAS - Powered by 360 Integral Solutions</footer>
      </section>
    </main>
  );
}
